// frontend/src/components/CuttingPlan/CuttingPlanForm.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { cuttingPlansAPI } from '../../api/client';
import { useTranslation } from '../../context/TranslationContext';
import { useToast } from '../../hooks/useToast';
import Toast from '../Common/Toast';
import { FiSave, FiX, FiDownload } from 'react-icons/fi';
import OrderSelection from './OrderSelection';
import SeparationPlanner from './SeparationPlanner';
import './CuttingPlanForm.css';

const CuttingPlanForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { planId } = useParams();
  const isEditMode = !!planId;
  
  const { toasts, removeToast, toast_success, toast_error } = useToast();

  const [loading, setLoading] = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [importing, setImporting] = useState(false);
  
  // Session identifier (could be IP or user session)
  const [sessionIp] = useState(() => `session_${Date.now()}`);
  
  // Form data
  const [formData, setFormData] = useState({
    modelCode: '',
    notes1: '',
    notes2: '',
    planningGroup: ''
  });

  // Orders and separations
  const [importedOrders,  setImportedOrders]  = useState(null);  // raw ERP rows from API
  const [selectedOrders,  setSelectedOrders]  = useState([]);   // checked production_order strings
  const [separations, setSeparations] = useState([]);

  useEffect(() => {
    if (isEditMode) {
      loadPlan();
    }
  }, [planId]);

  const loadPlan = async () => {
    try {
      setLoading(true);
      const response = await cuttingPlansAPI.getById(planId);
      
      if (response.data.success) {
        const { header, lines, orders } = response.data.data;
        
        setFormData({
          modelCode: header.plan_number?.split('-')[0] || '',
          notes1: header.notes_1 || '',
          notes2: header.notes_2 || '',
          planningGroup: header.planning_group || ''
        });

        // Group lines by separation sequence
        const groupedSeparations = lines.reduce((acc, line) => {
          const seq = line.separation_sequence;
          if (!acc[seq]) {
            acc[seq] = {
              sequence: seq,
              fabricLayers: line.fabric_layers,
              lines: []
            };
          }
          acc[seq].lines.push(line);
          return acc;
        }, {});

        setSeparations(Object.values(groupedSeparations));
      }
    } catch (err) {
      console.error('Error loading plan:', err);
      toast_error(t('messages.error_load', 'Failed to load plan'));
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImportFromErp = async () => {
    const group = formData.planningGroup?.trim();
    if (!group) {
      toast_error(t('cutting_plans.planning_group_required', 'Enter a planning group first'));
      return;
    }
    try {
      setImporting(true);
      const response = await cuttingPlansAPI.loadOrdersFromErp({
        planningGroup: group,
        sessionIp,
      });
      if (response.data.success) {
        toast_success(response.data.message || t('cutting_plans.orders_imported', 'Orders imported'));
        setImportedOrders(response.data.data || []);  // feed into OrderSelection checkbox list
      }
    } catch (err) {
      toast_error(err.response?.data?.error || t('messages.error_load', 'Failed to import orders'));
    } finally {
      setImporting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.modelCode) {
      toast_error(t('validation.model_code_required', 'Model code is required'));
      return;
    }

    if (separations.length === 0) {
      toast_error(t('validation.separations_required', 'At least one separation is required'));
      return;
    }

    try {
      setSaving(true);

      const data = {
        sessionIp,
        modelCode: formData.modelCode,
        notes1: formData.notes1,
        notes2: formData.notes2,
        planningGroup: formData.planningGroup,
        separations
      };

      const response = await cuttingPlansAPI.create(data);

      if (response.data.success) {
        toast_success(response.data.message);
        navigate('/cutting-plans');
      }
    } catch (err) {
      console.error('Error saving plan:', err);
      toast_error(err.response?.data?.error || t('messages.error_save', 'Failed to save plan'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading">{t('loading', 'Loading...')}</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="cutting-plan-form">
        <div className="form-header">
          <div>
            <h2>
              {isEditMode 
                ? t('cutting_plans.edit', 'Edit Cutting Plan') 
                : t('cutting_plans.create', 'Create Cutting Plan')}
            </h2>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Header Section */}
          <div className="form-section">
            <h3 className="section-title-medium">
              {t('cutting_plans.header_info', 'Plan Information')}
            </h3>
            
            <div className="form-grid form-grid-4">
              <div className="form-group">
                <label>{t('cutting_plans.model_code', 'Model Code')} *</label>
                <input
                  type="text"
                  value={formData.modelCode}
                  onChange={(e) => handleFieldChange('modelCode', e.target.value)}
                  maxLength={20}
                  required
                />
              </div>

              <div className="form-group">
                <label>{t('cutting_plans.notes_1', 'Notes 1')}</label>
                <input
                  type="text"
                  value={formData.notes1}
                  onChange={(e) => handleFieldChange('notes1', e.target.value)}
                  maxLength={50}
                />
              </div>

              <div className="form-group">
                <label>{t('cutting_plans.notes_2', 'Notes 2')}</label>
                <input
                  type="text"
                  value={formData.notes2}
                  onChange={(e) => handleFieldChange('notes2', e.target.value)}
                  maxLength={50}
                />
              </div>

              <div className="form-group">
                <label>{t('cutting_plans.planning_group', 'Planning Group')}</label>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input
                    type="text"
                    value={formData.planningGroup}
                    onChange={(e) => handleFieldChange('planningGroup', e.target.value)}
                    maxLength={50}
                    placeholder={t('cutting_plans.planning_group_placeholder', 'e.g. G001,G002')}
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    className="btn-primary btn-sm"
                    onClick={handleImportFromErp}
                    disabled={importing || !formData.planningGroup?.trim()}
                    title={t('cutting_plans.import_from_erp', 'Import production orders from ERP')}
                    style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                  >
                    <FiDownload size={14}/>
                    {importing
                      ? t('cutting_plans.importing', 'A importar…')
                      : t('cutting_plans.import_erp', 'Importar ERP')}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Order Selection Section */}
          {!isEditMode && (
            <div className="form-section">
              <OrderSelection
                sessionIp={sessionIp}
                importedOrders={importedOrders}
                onOrdersSelected={setSelectedOrders}
              />
            </div>
          )}

          {/* Separation Planning Section */}
          <div className="form-section">
            <SeparationPlanner
              sessionIp={sessionIp}
              selectedOrders={selectedOrders}
              separations={separations}
              onSeparationsChange={setSeparations}
            />
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate('/cutting-plans')}
              className="btn-secondary"
            >
              <FiX size={16} />
              {t('cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={saving}
            >
              <FiSave size={16} />
              {saving ? t('saving', 'Saving...') : t('save', 'Save')}
            </button>
          </div>
        </form>
      </div>

      {/* Toast notifications */}
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </div>
  );
};

export default CuttingPlanForm;